/**
 * web/src/lib/celebrate.ts — one confetti contract (card psHEfEkK9cT2).
 *
 * Jonathan's email 2026-11-04: confetti still fired on every app open, and
 * "fire" belongs to a shared win, not to a page load. Two rules live here so
 * every surface obeys the same ones:
 *
 *   1. A celebration is an EVENT, never history. `celebrateOnce(key)` dedupes
 *      by key for the life of the page, so a remount or a reload replaying the
 *      same win cannot burst twice.
 *   2. The preference is one key, read by both the layer that paints the
 *      particles AND the Settings switch that turns them off — they cannot
 *      disagree. Default is ON (localStorage absent = enabled).
 *
 * Deliberately device-local: it is a motion preference, not couple data, so it
 * needs no schema and no server round trip.
 */
import { useEffect, useState } from 'react';

/** localStorage key, shared with the Settings switch (Settings › You). */
export const CONFETTI_PREF_KEY = 'pear.settings.confetti';

/** Window event the layer listens for. Detail is `{ key: string }`. */
export const CELEBRATE_EVENT = 'pear:celebrate';

/** Fired when the preference changes, so an open switch and layer agree. */
export const CONFETTI_PREF_EVENT = 'pear:confetti-pref';

export function confettiEnabled(): boolean {
  try {
    // Anything but an explicit '0' keeps the default-on behaviour.
    return localStorage.getItem(CONFETTI_PREF_KEY) !== '0';
  } catch {
    return true; // private mode: never break a celebration on a storage throw
  }
}

export function setConfettiEnabled(on: boolean): void {
  try {
    localStorage.setItem(CONFETTI_PREF_KEY, on ? '1' : '0');
  } catch {
    /* private mode — the in-session event below still applies */
  }
  window.dispatchEvent(new CustomEvent(CONFETTI_PREF_EVENT, { detail: { on } }));
}

/** React binding for the Settings switch (and anything else that gates). */
export function useConfettiEnabled(): boolean {
  const [on, setOn] = useState<boolean>(confettiEnabled);
  useEffect(() => {
    const sync = () => setOn(confettiEnabled());
    window.addEventListener(CONFETTI_PREF_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CONFETTI_PREF_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return on;
}

/** Keys already celebrated in this page load — the once-only guard. */
const celebrated = new Set<string>();

/**
 * Ask for one burst. Silent when the preference is off or this key has
 * already fired. `key` must identify the MOMENT (a message seq, a round id),
 * never a screen — a screen key re-fires on every open, which is the bug.
 */
export function celebrateOnce(key: string): void {
  if (!confettiEnabled()) return;
  if (celebrated.has(key)) return;
  celebrated.add(key);
  window.dispatchEvent(new CustomEvent(CELEBRATE_EVENT, { detail: { key } }));
}
